// @flow

import * as React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {InputBase, TextInput} from 'sail/Input';
import {FormattedMessage, useIntl} from 'react-intl';
import {noTranslateMessages} from 'docs/intl/noTranslate';
import {Table, TableHeaderCell, TableCell} from '../../../../core/Table/Table';
import {InlineCode} from '../../../../core/InlineCode/InlineCode';
import './TaxCodesTable.scss';

type TaxCodeEntryType = {|
  id: string,
  name: string,
  description: string,
|};

type TaxCodesTableProps = {|
  entries: Array<TaxCodeEntryType>,
  children?: React.Node,
|};

export const TaxCodesTable = ({entries}: TaxCodesTableProps) => {
  const [filter, setFilter] = React.useState('');
  const intl = useIntl();

  if (!entries) {
    return null;
  }

  const translatedEntries = entries.map((entry) => {
    const messages = noTranslateMessages({
      short_name: {
        id: `docsApp.tax_code_details.${entry.id}.name`,
        defaultMessage: entry.name,
      },
      description: {
        id: `docsApp.tax_code_details.${entry.id}.description`,
        defaultMessage: entry.description,
      },
    });
    const description = intl.formatMessage(messages.description);
    const name = intl.formatMessage(messages.short_name);
    return {
      id: entry.id,
      description,
      name,
    };
  });

  const filteredEntries = translatedEntries.filter((entry) => {
    const filterText = filter.trim().toLowerCase();

    return (
      entry.name.toLowerCase().includes(filterText) ||
      entry.id.toLowerCase().includes(filterText) ||
      entry.description.toLowerCase().includes(filterText)
    );
  });

  const placeholderMessage = {
    id: 'docsApp.components.taxCodeDetails.search.searchTaxCodes',
    description: 'placesholder for tax category search box',
    defaultMessage: 'Search tax categories...',
  };

  return (
    <Box margin={{top: 16}}>
      <Group direction="vertical">
        <InputBase>
          <TextInput
            type="search"
            onChange={setFilter}
            value={filter}
            placeholder={intl.formatMessage(placeholderMessage)}
            width={300}
          />
        </InputBase>
        <Table type="striped">
          <thead>
            <tr>
              <TableHeaderCell className="TaxCodesTableCellID">
                <FormattedMessage
                  id="docsApp.components.taxCodeDetails.column.taxCodeId"
                  description="column header for the tax codes"
                  defaultMessage="Tax code"
                />
              </TableHeaderCell>
              <TableHeaderCell className="TaxCodesTableCellName">
                <FormattedMessage
                  id="docsApp.components.taxCodeDetails.column.taxCodeName"
                  description="column header for the category names associated with tax codes"
                  defaultMessage="Category name"
                />
              </TableHeaderCell>
              <TableHeaderCell>
                <FormattedMessage
                  id="docsApp.components.taxCodeDetails.column.taxCategoryDescription"
                  description="column header for the uses of the tax codes"
                  defaultMessage="Use this tax category for"
                />
              </TableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.length !== 0 ? (
              filteredEntries.map((entry) => {
                return (
                  <tr>
                    <TableCell className="TaxCodesTableCellID">
                      <InlineCode>{entry.id}</InlineCode>
                    </TableCell>
                    <TableCell className="TaxCodesTableCellName">
                      {entry.name}
                    </TableCell>
                    <TableCell>{entry.description}</TableCell>
                  </tr>
                );
              })
            ) : (
              <tr>
                <TableCell className="TaxCodesTableCellName" colspan={3}>
                  <FormattedMessage
                    id="docsApp.components.taxCategoryDetails.notFound"
                    description="Error message when no tax categories are found"
                    defaultMessage="No matching tax categories found"
                  />
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </Group>
    </Box>
  );
};
